import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { API_V1_ROOT } from './serviceConfig';

const API_BASE_URL = API_V1_ROOT;

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(credentials: { username: string; password: string }) {
    const formData = new URLSearchParams();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);

    const response = await this.api.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  }

  async register(userData: any) {
    const response = await this.api.post('/auth/register', userData);
    return response.data;
  }

  async getCurrentUser() {
    const response = await this.api.get('/auth/me');
    return response.data;
  }

  // User endpoints
  async getUsers(params?: { skip?: number; limit?: number }) {
    const response = await this.api.get('/users/', { params });
    return response.data;
  }

  async getUser(userId: number) {
    const response = await this.api.get(`/users/${userId}`);
    return response.data;
  }

  async createUser(userData: any) {
    const response = await this.api.post('/users/', userData);
    return response.data;
  }

  async updateUser(userId: number, userData: any) {
    const response = await this.api.put(`/users/${userId}`, userData);
    return response.data;
  }

  async deleteUser(userId: number) {
    const response = await this.api.delete(`/users/${userId}`);
    return response.data;
  }

  // Job endpoints
  async getJobs(params?: { 
    skip?: number; 
    limit?: number; 
    assigned_to?: number; 
    status?: string 
  }) {
    const response = await this.api.get('/jobs/', { params });
    return response.data;
  }

  async getAvailableJobs(limit: number = 50) {
    const response = await this.api.get('/jobs/available', { params: { limit } });
    return response.data;
  }

  async getJob(jobId: number) {
    const response = await this.api.get(`/jobs/${jobId}`);
    return response.data;
  }

  async createJob(jobData: any) {
    const response = await this.api.post('/jobs/', jobData);
    return response.data;
  }

  async updateJob(jobId: number, jobData: any) {
    const response = await this.api.put(`/jobs/${jobId}`, jobData);
    return response.data;
  }

  async assignJob(jobId: number, assignment: { assigned_to: number }) {
    const response = await this.api.post(`/jobs/${jobId}/assign`, assignment);
    return response.data;
  }

  async startJob(jobId: number) {
    const response = await this.api.post(`/jobs/${jobId}/start`);
    return response.data;
  }

  // Document endpoints
  async getDocumentByJob(jobId: number) {
    const response = await this.api.get(`/documents/job/${jobId}`);
    return response.data;
  }

  async createDocument(documentData: any) {
    const response = await this.api.post(`/documents/job/${documentData.job_id}`, documentData);
    return response.data;
  }

  async updateDocument(documentId: number, documentData: any) {
    const response = await this.api.put(`/documents/${documentId}`, documentData);
    return response.data;
  }

  async submitDocument(documentId: number) {
    const response = await this.api.post(`/documents/${documentId}/submit`);
    return response.data;
  }

  // Health check
  async healthCheck() {
    const response = await this.api.get('/health');
    return response.data;
  }

  // Pharmacy endpoints
  async searchMedications(query: string) {
    const response = await this.api.get('/pharmacy/medications/search', { 
      params: { q: query } 
    });
    return response.data;
  }

  async getMedication(medicationId: number) {
    const response = await this.api.get(`/pharmacy/medications/${medicationId}`);
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
